'use client';

import { useId } from 'react';

import type { OrderInfo } from '@eliadmin/core/src/api/claims/claims-api';
import { CARD, TEXT_LINK } from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import { orderStep } from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import {
  isToday,
  orderDate,
  orderTime,
} from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import { Icon } from '@eliadmin/core/src/components/nt/ui/icon';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

type StepState = 'done' | 'current' | 'todo' | 'cancelled';

type Step = { label: string; state: StepState; time?: string };

/** Figma step marker: check-circle when done, clock in progress, 28 px #E2E5DF dot ahead. */
function StepMarker({ state }: { state: StepState }) {
  if (state === 'todo') return <span className="size-7 shrink-0 rounded-full bg-[#E2E5DF]" />;
  return (
    <Icon
      name={state === 'done' ? 'check-circle' : state === 'current' ? 'clock' : 'x-circle'}
      size={28}
      className={state === 'cancelled' ? 'text-[#B42318]' : 'text-[#127A26]'}
    />
  );
}

/**
 * Figma „Status timeline“ (208:12024): four steps side by side from a 36rem card, a vertical
 * track narrower. Steps reached are Body/M Bold, the rest Body/M #5D655D; the line after a step is
 * green once the next step is reached. Times only from data: the creation time under the first
 * step – the API has no times (or estimates) of the later steps.
 */
function Track({ steps }: { steps: Step[] }) {
  return (
    <ol className="flex flex-col @[36rem]:flex-row @[36rem]:gap-3">
      {steps.map((step, index) => {
        const last = index === steps.length - 1;
        const reached = step.state !== 'todo';
        // The line after a step is green once the next step is reached.
        const next = steps[index + 1];
        return (
          <li
            key={`${index}-${step.label}`}
            aria-current={step.state === 'current' ? 'step' : undefined}
            className="flex min-w-0 gap-3 @[36rem]:flex-1 @[36rem]:flex-col @[36rem]:gap-2"
          >
            <div className="flex flex-col items-center gap-1 @[36rem]:flex-row @[36rem]:gap-2">
              <StepMarker state={step.state} />
              {!last ? (
                <span
                  aria-hidden="true"
                  className={cn(
                    'min-h-3 w-0.5 flex-1 rounded-[1px] @[36rem]:h-0.5 @[36rem]:min-h-0 @[36rem]:w-auto',
                    next?.state === 'done' || next?.state === 'current'
                      ? 'bg-[#127A26]'
                      : 'bg-[#E2E5DF]',
                  )}
                />
              ) : null}
            </div>
            <div
              className={cn(
                'flex min-w-0 flex-col gap-1 pt-1 @[36rem]:pb-0 @[36rem]:pt-0',
                !last && 'pb-4',
              )}
            >
              <span
                className={cn(
                  reached ? 'nt-body-m-bold text-[#0D110D]' : 'nt-body-m text-[#5D655D]',
                )}
              >
                {step.label}
              </span>
              {step.time ? <span className="nt-caption text-[#5D655D]">{step.time}</span> : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Figma „Card · Stav objednávky“ (208:12024): H5 title, „Sledovať zásielku“ with the truck when
 * the carrier link exists, and the track of the admin steps („Objednávka - stavy“). A cancelled
 * order shows the first step and the shop's cancelled status instead of the steps ahead.
 */
export function OrderTimeline({
  info,
  steps,
  tracking,
  trackLabel,
}: {
  info: Pick<OrderInfo, 'status' | 'created_at'>;
  steps: string[];
  tracking: string;
  trackLabel: string;
}) {
  const { t } = useTranslations();
  const titleId = useId();
  const current = orderStep(info.status, steps);
  const created = isToday(info.created_at)
    ? `${t('text992')} ${orderTime(info.created_at)}`
    : `${orderDate(info.created_at)} ${orderTime(info.created_at)}`.trim();

  const track: Step[] =
    current < 0
      ? [
          ...(steps[0] ? [{ label: steps[0], state: 'done' as const, time: created }] : []),
          { label: info.status.name, state: 'cancelled' },
        ]
      : steps.map((label, index) => ({
          label,
          state:
            index < current || (index === current && index === steps.length - 1)
              ? 'done'
              : index === current
                ? 'current'
                : 'todo',
          time: index === 0 ? created : undefined,
        }));

  return (
    <section aria-labelledby={titleId} className={cn(CARD, 'flex flex-col gap-4 p-4 md:p-6')}>
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <h2 id={titleId} className="nt-h5 text-[#0D110D]">
          {t('text245')}
        </h2>
        {tracking ? (
          <a
            href={tracking}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(TEXT_LINK, 'inline-flex items-center gap-1.5')}
          >
            <Icon name="truck" size={18} />
            {trackLabel}
          </a>
        ) : null}
      </div>
      {track.length ? <Track steps={track} /> : null}
    </section>
  );
}
